<template>
    <cart-template :isSales="true" :customer="customer">
        <template #configuration>
            <p class="cart-config-title mt-7 mb-6">
                {{ $l10n.__('Abonnement de') }} {{ customer.name }}
            </p>
            <el-form
                ref="saleCartForm"
                labelPosition="top"
                :rules="setRules"
                :model="modelForm"
                class="mb-8"
            >
                <date-config store="sale"></date-config>

                <h1>{{ $l10n.__("Configuration de l'offre") }}</h1>

                <article class="pb-1">
                    <header class="flex-level ai-center mt-6 mb-4">
                        <h2 class="m-0">{{ $l10n.__('Le plan') }}</h2>
                        <a href="https://go.sellsy.com/tarifs/" target="_blank" class="ml-3 link">{{
                            $l10n.__('Comparer les plans')
                        }}</a>
                    </header>
                    <el-form-item class="mb-0">
                        <el-radio-group
                            ref="licence-plan-radio"
                            v-model="licencePlan"
                            @change="licencePlan = $event"
                        >
                            <el-radio v-for="plan in plans" :key="plan.code" :label="plan.code">
                                <tag-label class="semibold uppercase" :bgColor="plan.color">
                                    <template #label>{{ $filters.upperCase(plan.name) }}</template>
                                </tag-label>
                            </el-radio>
                        </el-radio-group>
                    </el-form-item>
                </article>

                <span class="separator block my-6"></span>

                <!-- Main Products -->
                <article>
                    <el-form-item prop="products" class="flex-column-content sales-cart__products">
                        <header class="flex-row jc-space ai-center mb-5">
                            <h2 class="m-0">{{ $l10n.__('Les licences') }}</h2>
                            <RadioInput
                                ref="recurrency-input"
                                mode="edit"
                                radioStyle="button"
                                :modelValue="recurrency"
                                :choices="recurrencies"
                                @update:model-value="updateRecurrency"
                            />
                        </header>
                        <LicenceProduct
                            v-for="product in availablesProducts"
                            :key="product.code"
                            ref="licence-product"
                            class="mb-5"
                            :modelValue="products[product.id]"
                            :product="product"
                            :plan="plan"
                            userType="sale"
                            :numformat="numformat"
                            data-test="licence-product"
                            @update:modelValue="updateProductQuantity"
                        >
                        </LicenceProduct>
                        <template #error="{ error }">
                            <div
                                class="absolute wp-10 mt-0 mb-5 alert alert-danger"
                                style="order: -1"
                            >
                                {{ error }}
                            </div>
                        </template>
                    </el-form-item>
                </article>

                <span class="separator block my-6"></span>

                <!-- Extras Modules -->
                <article>
                    <el-form-item class="sales-cart__products">
                        <header>
                            <h2 class="mb-5">{{ $l10n.__('Modules supplémentaires') }}</h2>
                        </header>
                        <BonusPack
                            v-for="product in [...availablesPacks, ...currentGroupedPacks]"
                            :key="product.code"
                            ref="bonus-pack"
                            class="mb-5"
                            :modelValue="products[product.id]"
                            :product="product"
                            :plan="plan"
                            :numformat="numformat"
                            userType="sale"
                            :sourceLimitation="false"
                            data-test="bonus-pack"
                            @update:modelValue="updateProductQuantity"
                            @changePackPlan="switchPackPlan"
                        >
                        </BonusPack>
                    </el-form-item>
                </article>

                <span class="separator block my-6"></span>

                <article>
                    <header>
                        <h2 class="mt-0 mb-5">{{ $l10n.__('Paiement') }}</h2>
                    </header>
                    <el-form-item :label="$t.__('common.labels.payment_method')">
                        <el-radio-group
                            ref="payment-method-input"
                            v-model="payment"
                            prop="paymentMethod"
                            @change="payment = $event"
                        >
                            <el-radio
                                v-for="payment in paymentMethods"
                                :key="payment.id"
                                class="black"
                                :label="payment.id"
                                :disabled="disableWithdrawal(payment.id)"
                                >{{ payment.label }}</el-radio
                            >
                        </el-radio-group>
                    </el-form-item>
                </article>

                <span class="separator block my-6"></span>

                <!-- Billing Contact -->
                <SamCartContactForm
                    ref="sam-cart-contact-form"
                    :modelValue="contact"
                    :errors="errors"
                    @update:modelValue="updateContactForm"
                />

                <span class="separator block my-6"></span>

                <!-- Billing Settings -->
                <h2 class="mt-0 mb-5">{{ $l10n.__('Informations de facturation') }}</h2>

                <!-- Billing Settings -- Local -->
                <Address v-model="addressSettings">
                    <template #manualAddress="{ data, updateCountry }">
                        <el-form-item
                            prop="billingSettings.line1"
                            :label="$t.__('common.labels.street_name')"
                        >
                            <el-input v-model="data.line1" autocomplete="off"></el-input>
                        </el-form-item>
                        <el-form-item :label="$t.__('common.labels.more_address_informations')">
                            <el-input v-model="data.line2" autocomplete="off"></el-input>
                        </el-form-item>
                        <div class="flex-row">
                            <el-form-item
                                class="wp-3"
                                prop="billingSettings.postalCode"
                                :label="$t.__('common.labels.postal_code')"
                            >
                                <el-input v-model="data.postalCode" autocomplete="off"></el-input>
                            </el-form-item>
                            <el-form-item
                                class="wp-7 ml-3"
                                prop="billingSettings.city"
                                :label="$t.__('common.labels.city')"
                            >
                                <el-input v-model="data.city" autocomplete="off"></el-input>
                            </el-form-item>
                        </div>
                        <div class="flex-row">
                            <el-form-item class="wp-5" :label="$t.__('common.labels.state')">
                                <el-input
                                    v-model="data.administrative"
                                    autocomplete="off"
                                ></el-input>
                            </el-form-item>
                            <el-form-item
                                class="wp-5 ml-3"
                                prop="billingSettings.country"
                                :label="$t.__('common.labels.country')"
                            >
                                <country-input
                                    :defaultValue="{
                                        code: data.countryCode,
                                        name: data.country,
                                    }"
                                    @change="updateCountry"
                                >
                                </country-input>
                            </el-form-item>
                        </div>
                    </template>
                    <template #algoliaWrapper="{ methods, props, data }">
                        <el-form-item
                            prop="billingSettings.line1"
                            :label="$t.__('common.labels.street_name')"
                            :class="{ hide: data.currentValue.manualMode }"
                        >
                            <AlgoliaInput
                                :elementName="props.modelValue._id"
                                placeType="address"
                                @bubble-address="methods.captureAddress"
                            />
                            <CompassButton
                                :label="props.manuallyLabel"
                                type="tertiary"
                                class="b-0 pl-0 mt-1"
                                @click="methods.switchMode"
                            />
                        </el-form-item>
                    </template>
                </Address>

                <!-- BillingSettings -- Language -->
                <div class="flex-row">
                    <el-form-item
                        class="wp-5"
                        prop="billingLocale"
                        :label="$t.__('common.labels.invoicing_language')"
                    >
                        <el-select
                            ref="billing-locale"
                            v-model="contact.billingLocale"
                            class="wp-10 fs-4"
                            @change="updateContactForm({ billingLocale: $event })"
                        >
                            <el-option
                                v-for="locale in billingLocales"
                                :key="locale.code"
                                :label="locale.name"
                                :value="locale.code"
                            >
                            </el-option>
                        </el-select>
                    </el-form-item>

                    <!-- BillingSettings -- TVA -->
                    <el-form-item
                        class="wp-5 ml-4"
                        :label="$t.__('common.labels.tax_number')"
                        prop="vatIntra"
                    >
                        <el-input
                            ref="vat-intra"
                            v-model="tvaIntra"
                            @input="tvaIntra = $event"
                        ></el-input>
                    </el-form-item>
                </div>

                <span class="separator block my-6"></span>

                <!-- Custom Message Settings -->
                <h2 class="mt-0 mb-5">{{ $l10n.__('Personnalisation des emails') }}</h2>

                <custom-message v-if="contact.name" ref="custom-message" context="sale">
                </custom-message>
            </el-form>
        </template>

        <!--  CART - Summary  -->
        <template #resume>
            <cart-summary context="sale" :userSale="true">
                <template #licences>
                    <ul class="ov-auto mb-0 cart-recap-products">
                        <!-- Products Summary -->
                        <li
                            v-for="(product, index) in productsWithLicences"
                            ref="products-summary"
                            :key="product.code"
                            class="flex-row"
                            :class="{ 'mb-2': index + 1 !== productsWithLicences.length }"
                            data-test="product-summary"
                        >
                            <div>
                                <p ref="product-quantity-summary" class="grey m-0 fs-3">
                                    {{
                                        $l10n.n__(
                                            '%(total)s licence %(productsName)s',
                                            '%(total)s licences %(productsName)s',
                                            products[product.id].quantity,
                                            {
                                                total: products[product.id].quantity,
                                                productsName: product.title,
                                            },
                                        )
                                    }}
                                </p>
                                <span
                                    v-if="freeLicencesByProduct(product.id)"
                                    ref="free-quantity-summary"
                                    class="grey"
                                    >{{
                                        $l10n.n__(
                                            'Dont %s offerte',
                                            'Dont %s offertes',
                                            freeLicencesByProduct(product.id),
                                            freeLicencesByProduct(product.id),
                                        )
                                    }}</span
                                >
                            </div>
                            <template v-if="products[product.id].total">
                                <p
                                    v-if="freeLicencesByProduct(product.id)"
                                    ref="product-discount-total-summary"
                                    class="flex-item-right my-0 semibold"
                                >
                                    <span class="strike grey semibold">{{
                                        $filters.numberFormatToFloatWithOptions(
                                            products[product.id].total.monthly.total_amount,
                                            numformat,
                                            true,
                                        )
                                    }}</span>
                                    <span class="success-50 fs-3 pl-2"
                                        >{{
                                            $filters.numberFormatWithOptions(
                                                products[product.id].total.monthly
                                                    .total_amount_discounted,
                                                numformat,
                                                true,
                                            )
                                        }}<span class="fs-2">{{ $l10n.__('/mois') }}</span></span
                                    >
                                </p>
                                <p
                                    v-else
                                    ref="product-total-summary"
                                    class="flex-item-right my-0 fs-3 semibold grey"
                                >
                                    {{
                                        $filters.numberFormatToFloatWithOptions(
                                            products[product.id].total.monthly
                                                .total_amount_discounted,
                                            numformat,
                                            true,
                                        )
                                    }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                </p>
                            </template>
                        </li>

                        <!-- Packs Summary -->
                        <li
                            v-for="(product, index) in [
                                ...packsWithLicences,
                                ...groupedPacksWithLicences,
                            ]"
                            :key="product.code"
                            ref="packs-summary"
                            class="flex-row"
                            :class="{
                                'mb-2':
                                    index + 1 !==
                                    [...packsWithLicences, ...groupedPacksWithLicences].length,
                                'mt-2': index === 0,
                            }"
                            data-test="packs-summary"
                        >
                            <div>
                                <p class="grey m-0 fs-3">
                                    {{
                                        $l10n.__('Module %(productsName)s', {
                                            productsName: !!product.groups.length
                                                ? product.printableName
                                                : product.title,
                                        })
                                    }}
                                </p>
                            </div>
                            <template v-if="products[product.id].total">
                                <!-- Discount detail -->
                                <p
                                    v-if="
                                        products[product.id].total.monthly.total_amount_discounted <
                                        products[product.id].total.monthly.total_amount
                                    "
                                    ref="packs-total-discount-summary"
                                    class="flex-item-right my-0 semibold"
                                >
                                    <span class="strike grey semibold">{{
                                        $filters.numberFormatToFloatWithOptions(
                                            products[product.id].total.monthly.total_amount,
                                            numformat,
                                            true,
                                        )
                                    }}</span>
                                    <span class="success-50 fs-3 pl-2">
                                        <template
                                            v-if="
                                                products[product.id].total.monthly
                                                    .total_amount_discounted !== 0
                                            "
                                        >
                                            {{
                                                $filters.numberFormatToFloatWithOptions(
                                                    products[product.id].total.monthly
                                                        .total_amount_discounted,
                                                    numformat,
                                                    true,
                                                )
                                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                        </template>
                                        <template v-else>{{ $l10n.__('Offert') }}</template>
                                    </span>
                                </p>
                                <!-- Base price -->
                                <p
                                    v-else
                                    ref="packs-total-summary"
                                    class="flex-item-right my-0 fs-3 semibold grey"
                                >
                                    {{
                                        $filters.numberFormatToFloatWithOptions(
                                            products[product.id].total.monthly
                                                .total_amount_discounted,
                                            numformat,
                                            true,
                                        )
                                    }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                </p>
                            </template>
                        </li>
                    </ul>
                </template>
                <template #subtotal>
                    <div class="flex-column fs-3 cart-recap-total-label">
                        <span class="mb-2">{{ $l10n.__('Total HT avant remise') }}</span>
                        <span class="mb-2">{{ $l10n.__('Remise') }}</span>
                        <span class="mb-2">{{ $l10n.__('Total HT') }}</span>
                        <span v-if="taxRate && taxRate !== 0" class="mb-2"
                            >{{ $l10n.__('TVA') }} {{ taxRate }}%</span
                        >
                    </div>
                    <div ref="subtotal-summary-yearly" class="flex-column aright fs-3">
                        <span class="mb-2"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/an') }}</span></span
                        >
                        <span class="mb-2"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_discount,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/an') }}</span></span
                        >
                        <span class="mb-2"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_discounted,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/an') }}</span></span
                        >
                        <span v-if="taxRate && taxRate !== 0" class="mb-2"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_vat,
                                    numformat,
                                    true,
                                )
                            }}
                        </span>
                    </div>
                    <div ref="subtotal-summary-monthly" class="flex-column aright fs-3">
                        <span class="mb-2"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span></span
                        >
                        <span class="mb-2"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount_discount,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span></span
                        >
                        <span class="mb-2"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount_discounted,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span></span
                        >
                        <span v-if="taxRate && taxRate !== 0" class="mb-2">{{
                            $filters.numberFormatToFloatWithOptions(
                                totalMonthly.total_amount_vat,
                                numformat,
                                true,
                            )
                        }}</span>
                    </div>
                </template>
                <template #total>
                    <div ref="summary-total" class="flex-row jc-space">
                        <p class="fs-4 my-0 bold cart-recap-total-label">
                            {{ $l10n.__('Total TTC') }}
                        </p>
                        <p class="fs-4 my-0 aright bold flex-row baseline">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_tax_included,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/an') }}</span>
                        </p>
                        <p class="fs-4 my-0 aright bold flex-row baseline">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount_tax_included,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                        </p>
                    </div>
                </template>
                <template #actions>
                    <CompassButton
                        ref="form-submit"
                        :label="$t.__('common.actions.send_to_client')"
                        type="primary"
                        :disabled="computeLoading"
                        :loading="computeLoading"
                        class="wp-10 mt-4"
                        @click="submitForm('saleCartForm', 'action')"
                    />
                </template>
            </cart-summary>
        </template>
    </cart-template>
</template>

<script>
import Address from 'vcomponents/inputs/custom/Address.vue';
import PricingV4Module from 'stores/sellsyaccountmanager/pricingv4';
import AddressRules from 'vcomponents/forms/rules/AddressRules.js';
import CountryInput from 'vcomponents/inputs/CountryInput.vue';
import AlgoliaInput from 'vcomponents/inputs/AlgoliaInput.vue';
import { createNamespacedHelpers } from 'vuex';
import VatIdentifierValidator from 'Services/VatIdentifierValidator';
import TagLabel from 'vcomponents/utilities/tags/TagLabel.vue';
import LicenceProduct from 'vcomponents/pages/account/LicenceProduct.vue';
import BonusPack from 'vcomponents/pages/account/BonusPack.vue';
import FormMixin from 'mixins/FormMixins';
import { fetchMetadata } from 'api/sellsyaccountmanager';

import DateConfig from 'vcomponents/pages/account/sale/DateConfig.vue';
import RadioInput from 'vcomponents/inputs/RadioInput.vue';
import SamCartContactForm from 'vcomponents/pages/account/SamCartContactForm.vue';
import CartTemplate from 'vcomponents/pages/account/CartTemplate.vue';
import CartSummary from 'vcomponents/pages/account/CartSummary.vue';
import CustomMessage from 'vcomponents/pages/account/sale/CustomMessage.vue';
import { goToUrl } from 'js/vue/helpers';
import Loader from 'vcomponents/utilities/Loader.vue';

const saleCart = createNamespacedHelpers('saleCart');
const corp = createNamespacedHelpers('corp');
export default {
    components: {
        SamCartContactForm,
        Address,
        AlgoliaInput,
        CountryInput,
        RadioInput,
        TagLabel,
        LicenceProduct,
        CartTemplate,
        CartSummary,
        BonusPack,
        Loader,
        CustomMessage,
        DateConfig,
    },
    mixins: [FormMixin],
    data() {
        return {
            rules: {
                name: [{ required: true, message: this.$l10n.__('Veuillez renseigner un nom') }],
                forename: [
                    { required: true, message: this.$l10n.__('Veuillez renseigner un prénom') },
                ],
                phone: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner un numéro de téléphone'),
                    },
                    {
                        min: 4,
                        message: this.$l10n.__('Veuillez renseigner un numéro de téléphone valide'),
                    },
                ],
                billingLocale: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner une langue de facturation'),
                    },
                ],
                email: [
                    {
                        required: true,
                        type: 'email',
                        message: this.$l10n.__('Veuillez renseigner une adresse email'),
                    },
                ],
                billingSettings: {
                    ...AddressRules,
                },
                vatIntra: [
                    {
                        validator: (rule, value, callback) => {
                            /**
                             * Check TVA Intra format with associated country, but no require.
                             */
                            if (
                                value === '' ||
                                _.isNil(value) ||
                                _.isUndefined(this.billingSettings.countryCode)
                            )
                                return callback();

                            if (_.isNull(this.billingSettings.countryCode))
                                return callback(new Error(this.$l10n.__('Aucun pays sélectionné')));

                            const validator = new VatIdentifierValidator(
                                this.billingSettings.countryCode,
                            );
                            if (!validator.isValid(value)) {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Le format du n° de tva ne correspond pas à celui du pays sélectionné',
                                        ),
                                    ),
                                );
                            }
                            callback();
                        },
                        trigger: 'change',
                    },
                ],
                products: [
                    {
                        validator: (rule, value, callback) => {
                            /**
                             * Cart must get 2 paid licences or 1 if allowed by permission
                             */
                            if (this.customer.allowsOneLicense && this.quantityPaidLicences < 1) {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Veuillez sélectionner au minimum 1 licence payante',
                                        ),
                                    ),
                                );
                            } else if (
                                !this.customer.allowsOneLicense &&
                                this.quantityPaidLicences < 2
                            ) {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Veuillez sélectionner au minimum 2 licences payantes',
                                        ),
                                    ),
                                );
                            }

                            callback();
                        },
                        trigger: 'change',
                    },
                ],
                paymentMethod: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner un moyen de paiement'),
                    },
                ],
            },
            customMessageValidator: {
                customMessage: [
                    {
                        validator: (rule, value, callback) => {
                            const customMsg = this.customMessage.match(/<p>(.*?)<\/p>/g);
                            if (!customMsg || customMsg[0].trim() === '<p></p>') {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Ce champ ne peut pas être vide lorsque vous indiquez vouloir joindre un message personnalisé',
                                        ),
                                    ),
                                );
                            }
                            callback();
                        },
                    },
                ],
            },
        };
    },
    computed: {
        ...corp.mapGetters(['corpName']),
        ...saleCart.mapGetters([
            'modelForm',
            'contact',
            'billingLocales',
            'billingSettings',
            'recurrencies',
            'recurrency',
            'vatIntra',
            'customer',
            'plans',
            'currentPlan',
            'availablesProducts',
            'availablesPacks',
            'currentGroupedPacks',
            'products',
            'productsWithLicences',
            'groupedPacksWithLicences',
            'packsWithLicences',
            'plan',
            'isYearly',
            'isMonthly',
            'freeLicencesByProduct',
            'totalYearly',
            'totalMonthly',
            'computeLoading',
            'taxRate',
            'paymentMethod',
            'paymentMethods',
            'labelPlan',
            'quantityFreeLicences',
            'quantityPaidLicences',
            'quantityLicences',
            'cartId',
            'numformat',
            'isCustomMessageUsed',
            'customMessage',
        ]),
        addressSettings: {
            get() {
                return this.billingSettings;
            },
            set(value) {
                this.$store.commit('saleCart/setBillingSettings', value);
                this.$store.dispatch('saleCart/computeCart');
            },
        },
        tvaIntra: {
            get() {
                return this.vatIntra;
            },
            set(value) {
                this.$store.commit('saleCart/setVatIntra', value);
                this.$store.dispatch('saleCart/computeCart');
            },
        },
        licencePlan: {
            get() {
                return this.currentPlan;
            },
            set(value) {
                this.$store.dispatch('saleCart/updatePlan', value);
            },
        },
        labelPlan() {
            return this.isYearly
                ? this.$l10n.__('Abonnement annuel')
                : this.$l10n.__('Abonnement mensuel');
        },
        payment: {
            get() {
                return this.paymentMethod;
            },
            set(value) {
                this.$store.commit('saleCart/setPaymentMethod', value);
            },
        },
        /**
         * Add custom message if messageUsed's value is true
         */
        setRules() {
            if (this.isCustomMessageUsed) {
                const newRules = Object.assign({ ...this.rules }, this.customMessageValidator);
                return newRules;
            } else {
                return this.rules;
            }
        },
    },
    created() {
        this.$store.registerModule('saleCart', PricingV4Module);
        this.setCustomerId(this.$route.params.customerId);
        this.setSource('sales');
        this.setUserContext(this.$route.meta.user_context);

        if (this.$route.params.id !== 'new') {
            this.fetchCart(this.$route.params.id);
        }
        this.computeCart();
    },
    beforeUnmount() {
        this.$store.unregisterModule('saleCart');
    },
    methods: {
        ...saleCart.mapMutations(['setCustomerId', 'setSource', 'setUserContext']),
        ...saleCart.mapActions([
            'fetchMetadata',
            'updateContact',
            'updateRecurrency',
            'updatePlan',
            'updateProductQuantity',
            'computeCart',
            'createCart',
            'fetchCart',
            'updateCart',
            'setMetadata',
            'switchPackPlan',
        ]),
        updateContactForm(e) {
            this.updateContact({
                ...this.contact,
                ...e,
            });
        },
        back() {
            history.back();
        },
        action() {
            if (this.$route.params.id !== 'new' || !_.isNil(this.cartId)) {
                // Update
                this.update();
            } else {
                this.create();
            }
        },
        create() {
            this.createCart()
                .then((res) => {
                    Push.success(this.$l10n.__('Panier envoyé avec succès'));
                    goToUrl(
                        this.$router.resolve({
                            name: 'third.overview.samCarts',
                            params: { type: 'client', id: this.$route.params.thirdId },
                        }).href,
                    );
                })
                .catch((errors) => {
                    this.errors.setErrors(errors);
                });
        },
        update() {
            this.updateCart(this.cartId)
                .then(() => {
                    Push.success(this.$l10n.__('Panier modifié avec succès'));
                    goToUrl(
                        this.$router.resolve({
                            name: 'third.overview.samCarts',
                            params: { type: 'client', id: this.$route.params.thirdId },
                        }).href,
                    );
                })
                .catch((errors) => {
                    this.errors.setErrors(errors);
                });
        },
        disableWithdrawal(id) {
            return id === 'manual' && this.isMonthly;
        },
    },
    async beforeRouteEnter(to, from, next) {
        await fetchMetadata(to.params.customerId, 'new', 'saas')
            .then((res) => {
                if (
                    res.billingSettings.stripeSubscription ||
                    res.billingSettings.stripeSubscriptionSchedule
                ) {
                    next({
                        name: 'sam.v4.upsell.cart.form',
                        params: {
                            id: 'new',
                            corpId: res.saasCorpId,
                            thirdId: to.params.thirdId,
                            customerId: to.params.customerId,
                        },
                    });
                }
                next((vm) => {
                    vm.setMetadata(res);
                });
            })
            .catch((err) => {
                next(false);
            });
    },
};
</script>

<style lang="scss">
@use '~@sellsy/styleguide/src/_variables' as *;

.sales-cart__products {
    .el-form-item__content {
        display: block !important;
    }
}
.custom-msg {
    .el-form + p {
        transition: margin 0.33s ease;
    }
    .editor-input .ProseMirror p {
        font-size: $big-font-size;
        line-height: $val-5;
    }
    .el-form-item.el-checkbox > label {
        margin-bottom: 0 !important;
    }
    .is-error {
        margin-bottom: 0 !important;
        .editor-input {
            border-color: $alert-50 !important;
        }
        .el-form-item__error {
            position: relative;
            line-height: 16px;
        }
        & + p {
            margin-top: $val-3 !important;
        }
    }
}
</style>
